import React, { useCallback, useEffect, useMemo, useState } from "react";

import { convert, LocalTime, ZonedDateTime } from "@js-joda/core";
import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import {
  AnyFilterExpressionInput,
  MaintenanceDto,
  SortDirection,
  SortInput,
  useGetMaintenanceByProjectIdLazyQuery,
  useGetObjectsByProjectQuery,
} from "gql/generated/types";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { TabPanel, TabView } from "primereact/tabview";
import { Link } from "react-router-dom";
import { T } from "services/lang/LanguageService";
import { DEFAULT_DATE_FORMATTER } from "utils/constants";
import { parseToLocalZoneDateTime } from "utils/time-utils";
import LazyTable from "../components/LazyTable";
import MaintenanceDialogButton from "../components/Maintenance/MaintenanceDialogButton";
import MaintenanceSummary from "../components/Maintenance/MaintenanceSummary";
import TableColumnSelection from "../components/TableColumnSelection";
import useCustomColumns from "../hooks/useCustomColumns";
import useProjectContext from "../hooks/useProjectContext";
import useTableFilter from "../hooks/useTableFilter";
import { initialFilter } from "../utils/const";
import { TableColumn } from "../utils/types";
import { apolloErrorToaster, renderTag } from "../utils/utils";

const SORT_FIELD = "submissionTime";

const Maintenance: React.FC = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [objectIds, setObjectIds] = useState<string[]>([]);
  const { projectId, project, userSettings } = useProjectContext();
  const [dateFrom, setDateFrom] = useState<Date>(
    convert(ZonedDateTime.now().minusYears(1)).toDate()
  );
  const [dateTo, setDateTo] = useState<Date>(
    convert(ZonedDateTime.now().with(LocalTime.MAX)).toDate()
  );
  const [tableOptions, setTableOptions] = useTableFilter("Maintenance", {
    sortField: SORT_FIELD,
    reqSortField: SORT_FIELD,
    sortOrder: -1,
  });

  const [getMaintenance, { data, loading, previousData }] = useGetMaintenanceByProjectIdLazyQuery({
    onError: apolloErrorToaster,
  });

  const [getIsoMaintenance] = useGetMaintenanceByProjectIdLazyQuery();

  const total =
    data?.allMaintenanceByProjectIdAndDateRangePaged.pageInfo?.total ??
    previousData?.allMaintenanceByProjectIdAndDateRangePaged.pageInfo?.total ??
    0;

  const maintenanceData = (data?.allMaintenanceByProjectIdAndDateRangePaged.items ??
    previousData?.allMaintenanceByProjectIdAndDateRangePaged.items ??
    []) as MaintenanceDto[];

  const renderService = useCallback(
    (maintenance: MaintenanceDto) => {
      const services =
        project?.mdmProjectById.configuration?.maintenanceConfiguration.interruptedServices;
      return services?.find(is => is?.id === maintenance.interruptedServices)?.title ?? "-";
    },
    [project?.mdmProjectById.configuration?.maintenanceConfiguration.interruptedServices]
  );

  const renderObjects = (maintenance: MaintenanceDto) => {
    return (
      <Button
        label={maintenance.objectIds?.length.toString()}
        icon="pi pi-building"
        text
        iconPos="right"
        size="small"
        className="w-min"
        onClick={() => {
          setDialogOpen(true);
          setObjectIds((maintenance.objectIds as string[]) ?? []);
        }}
      />
    );
  };

  const renderActions = (rowData: MaintenanceDto) => (
    <Link to={`${rowData.id}`}>
      <Button size="small" type="button" icon="pi pi-pencil" text rounded />
    </Link>
  );

  const columns = useMemo(
    (): TableColumn<MaintenanceDto>[] => [
      {
        field: "title",
        header: T("word.title"),
        sortable: true,
        columnKey: "title",
      },
      {
        field: "status",
        header: T("word.status"),
        sortable: true,
        body: renderTag,
        columnKey: "status",
      },
      {
        field: "submissionTime",
        header: T("word.submission-time"),
        sortable: true,
        body: rowData =>
          rowData.submissionTime &&
          parseToLocalZoneDateTime(rowData.submissionTime).format(DEFAULT_DATE_FORMATTER),
        columnKey: "submissionTime",
      },
      {
        field: "approvalTime",
        header: T("word.approval-time"),
        sortable: true,
        body: rowData =>
          rowData.approvalTime &&
          parseToLocalZoneDateTime(rowData.approvalTime).format(DEFAULT_DATE_FORMATTER),
        columnKey: "approvalTime",
      },
      {
        field: "disconnectionTime",
        header: T("word.disconnection-time"),
        sortable: true,
        body: rowData =>
          rowData.disconnectionTime &&
          parseToLocalZoneDateTime(rowData.disconnectionTime).format(DEFAULT_DATE_FORMATTER),
        columnKey: "disconnectionTime",
      },
      {
        field: "reconnectionTime",
        header: T("word.reconnection-time"),
        sortable: true,
        body: rowData =>
          rowData.reconnectionTime &&
          parseToLocalZoneDateTime(rowData.reconnectionTime).format(DEFAULT_DATE_FORMATTER),
        columnKey: "reconnectionTime",
      },
      {
        field: "interruptedServices",
        header: T("word.interrupted-services"),
        body: renderService,
        sortable: true,
        columnKey: "interruptedServices",
      },
      {
        field: "reason",
        header: T("word.reason"),
        sortable: true,
        columnKey: "reason",
      },
      {
        field: "objectIds",
        header: T("mdm.objects"),
        body: renderObjects,
        columnKey: "objectIds",
      },
      {
        header: T("word.actions"),
        body: renderActions,
        columnKey: "actions",
        hideInFile: true,
      },
    ],
    [renderService]
  );

  const [
    customColumns,
    setCustomColumns,
    formattedColumns,
    mapCols,
    formatObjectFields,
    handleColumnSave,
    updateLoading,
  ] = useCustomColumns("Maintenance", columns, maintenanceData, tableOptions.rows);

  const fetchData = useCallback(
    async (
      take: number | null = null,
      skip: number | null = null,
      sort: SortInput[] | null = null,
      filters: AnyFilterExpressionInput[] | null = null,
      cacheAndNetwork: boolean = false,
      filter: AnyFilterExpressionInput | null = null,
      isolatedFetch: boolean = false
    ) => {
      const fetchFn = isolatedFetch ? getIsoMaintenance : getMaintenance;
      const resp = await fetchFn({
        variables: {
          input: {
            id: projectId!,
            range: {
              start: dateFrom,
              startInclusive: true,
              end: dateTo,
              endInclusive: true,
            },
            take: take ?? tableOptions.rows,
            skip: skip ?? tableOptions.first,
            sort: sort ?? [
              {
                property: tableOptions.reqSortField,
                direction: tableOptions.sortOrder === 1 ? SortDirection.Asc : SortDirection.Desc,
              },
            ],
            andFilters: filters ?? tableOptions.andFilters,
            filter: filter ?? tableOptions.filter,
          },
        },
        fetchPolicy: cacheAndNetwork ? "cache-and-network" : "cache-first",
      });
      return resp.data;
    },
    [
      dateFrom,
      dateTo,
      getIsoMaintenance,
      getMaintenance,
      projectId,
      tableOptions.andFilters,
      tableOptions.filter,
      tableOptions.first,
      tableOptions.reqSortField,
      tableOptions.rows,
      tableOptions.sortOrder,
    ]
  );

  useEffect(() => {
    if (userSettings) {
      fetchData(tableOptions.rows);
    }
  }, [tableOptions, userSettings, fetchData]);

  const resetFilters = () => {
    setTableOptions(prev => ({
      ...initialFilter,
      rows: prev.rows,
      sortField: SORT_FIELD,
      reqSortField: SORT_FIELD,
      sortOrder: -1,
    }));
  };

  const handleSearch = (value: string) => {
    setTableOptions(prev => ({
      ...prev,
      filter: null, // value ? searchAnalyticsFilter(value) : null,
      first: 0,
      search: value,
    }));
  };

  const extraTools = (
    <>
      <TableColumnSelection
        customColumns={customColumns}
        tableName="Maintenance"
        setCustomColumns={setCustomColumns}
        handleColumnSave={handleColumnSave}
        mapCol={mapCols}
        loading={updateLoading}
      />
      <MaintenanceDialogButton />
    </>
  );

  const centerTools = (
    <div className="flex gap-3">
      <div className="flex align-items-center gap-2 w-12rem">
        <strong>{T("word.from")}</strong>
        <CalendarWrapper value={dateFrom} onChange={setDateFrom} showTime={false} />
      </div>
      <div className="flex align-items-center gap-2 w-12rem">
        <strong>{T("word.to")}</strong>
        <CalendarWrapper
          value={dateTo}
          onChange={date => setDateTo(new Date(date.setHours(23, 59, 59, 999)))}
          showTime={false}
        />
      </div>
    </div>
  );

  return (
    <TabView>
      <TabPanel header={T("word.main")}>
        <LazyTable
          columns={formattedColumns}
          data={formatObjectFields}
          tableName="Maintenance"
          buttons={extraTools}
          centerTools={centerTools}
          containerName="MdmMaintenance"
          loading={loading}
          setTableOptions={setTableOptions}
          resetFilters={resetFilters}
          tableOptions={tableOptions}
          totalItems={total}
          fetchData={fetchData}
          onSearch={handleSearch}
          setCustomColumns={setCustomColumns}
        />
        <Dialog header={T("mdm.objects")} visible={dialogOpen} onHide={() => setDialogOpen(false)}>
          <ObjectList objectIds={objectIds} />
        </Dialog>
      </TabPanel>
      <TabPanel header={T("word.summary")}>
        <MaintenanceSummary data={maintenanceData} />
      </TabPanel>
    </TabView>
  );
};

export default Maintenance;

const ObjectList: React.FC<{ objectIds: string[] }> = ({ objectIds }) => {
  const { projectId } = useProjectContext();
  const { data, loading } = useGetObjectsByProjectQuery({
    variables: {
      id: projectId!,
    },
    skip: !projectId,
    onError: apolloErrorToaster,
  });

  if (loading) return <ProgressSpinner className="m-5" />;

  const objects = data?.mdmObjectsByProjectId.filter(o => objectIds.includes(o.id!)) ?? [];

  return (
    <div className="flex flex-column gap-3 m-3" style={{ maxHeight: "500px" }}>
      {objects.map(o => (
        <div key={o.id} className="flex flex-column">
          <Link to={`../objects/${o.id}`} className="hover:underline">
            {o.identifier}:
          </Link>
          <span>{o.title}</span>
        </div>
      ))}
    </div>
  );
};
